import React, { useState } from 'react';
import { useCustomers } from '../hooks/useCustomers';
import { Users } from 'lucide-react';
import Pagination from '../components/Pagination';
import LoadingSpinner from '../components/LoadingSpinner';
import useDebounce from '../hooks/useDebounce';

const Customers = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce search
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const filters = {};
  if (debouncedSearchQuery) filters.search = debouncedSearchQuery;

  const { data: customersData, isLoading } = useCustomers(currentPage, filters);
  const customers = customersData?.customers || [];
  const pagination = customersData?.pagination;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-prose-brand">Customers</h1>
          <p className="text-xs sm:text-sm text-prose-black/60 mt-1">Manage your customer list</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-300 p-3 sm:p-4 md:p-6">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-prose-black mb-2">Search by Name, Email or Phone</label>
          <input
            type="text"
            placeholder="e.g., John, john@email.com, +234701234567..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand/50 text-xs sm:text-sm"
          />
        </div>

        {/* Clear Search Button */}
        {searchQuery && (
          <button
            onClick={() => {
              setSearchQuery('');
              setCurrentPage(1);
            }}
            className="mt-3 px-3 py-1.5 text-xs font-medium text-prose-brand border border-prose-brand hover:bg-prose-brand hover:text-white rounded-lg transition duration-200"
          >
            Clear Search
          </button>
        )}
      </div>
      {/* Customers Table */}
      {isLoading ? (
        <LoadingSpinner message="Loading customers..." />
      ) : (
      <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-100 border-b border-gray-300">
              <tr>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">
                  Name
                </th>
                <th className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">Email</th>
                <th className="hidden md:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">Phone</th>
                <th className="hidden lg:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">Address</th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">Joined</th>
              </tr>
            </thead>
            <tbody>
              {customers.length > 0 ? (
                customers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="border-b border-gray-300/60 hover:bg-gray-50 transition duration-200"
                  >
                    <td className="px-3 sm:px-6 py-3 sm:py-4 font-medium text-prose-black text-xs sm:text-sm">{customer.name}</td>
                    <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 text-prose-black text-xs sm:text-sm">{customer.email}</td>
                    <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4 text-prose-black text-xs sm:text-sm">{customer.phone}</td>
                    <td className="hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4 text-prose-black text-xs sm:text-sm">{customer.address}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-prose-black/80 text-xs sm:text-sm">
                      {new Date(customer.created_at).toLocaleDateString('en-GB')}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-3 sm:px-6 py-12 sm:py-16">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="p-3 sm:p-4 rounded-full bg-gray-100">
                        <Users size={32} className="text-prose-black/40" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm sm:text-base font-semibold text-prose-black/70">No customers found</p>
                        <p className="text-xs sm:text-sm text-prose-black/50 mt-1">
                          {searchQuery
                            ? 'Try adjusting your search criteria'
                            : 'Your customer list will appear here'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer with Pagination */}
        {customers.length > 0 && (
          <div className="bg-white border-t border-gray-300 px-3 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-end gap-3 sm:gap-4 flex-col sm:flex-row">
              <Pagination
                pagination={pagination}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default Customers;
